/**
 * Shared PlateJS export utilities.
 * Used by PolicyDocuments, PolicyGenerationStep, and BrandedProposalEditor.
 * Exports PDF (jsPDF) and DOCX (docx library) from PlatePage[] + BrandColors.
 */

import type { PlatePage } from '@/altzero_plugins/features/scopingai/scopingAi/altzero_plugins/features/template-editor/Editor/features/Editor/components/PlateEditor/types';
import type { BrandingForPlate } from '@/altzero_plugins/features/scopingai/scopingAi/altzero_plugins/features/template-editor/Editor/features/Editor/utils/markdownToPlate';
import {
  DEFAULT_APP_FONT,
  googleFontsStylesheetHref,
  toCssFontFamily,
} from './fontFamilyCss';
import {
  formatPageNumberLabel,
  getContentPageCount,
  getCoverTocPageCount,
} from './pageNumbering';

export type BrandColors = {
  primary: string;
  secondary: string;
  fontColor: string;
  fontFamily: string;
  primaryRgb: [number, number, number];
  secondaryRgb: [number, number, number];
  fontColorRgb: [number, number, number];
  primaryDocx: string;
  secondaryDocx: string;
  fontColorDocx: string;
};

const hexToRgb = (hex: string): [number, number, number] => {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return [31, 41, 55];
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

export const buildBrandColors = (branding: BrandingForPlate | null): BrandColors => {
  const stripHash = (hex?: string | null) =>
    hex ? hex.replace('#', '').toUpperCase() : undefined;
  // Policy fonts are forced to black — branding colours are intentionally ignored.
  const primary   = '#000000';
  const secondary = '#000000';
  const fontColor = '#000000';
  const fontFamily = branding?.font_family?.trim() || DEFAULT_APP_FONT;
  return {
    primary,
    secondary,
    fontColor,
    fontFamily,
    primaryRgb:   hexToRgb(primary),
    secondaryRgb: hexToRgb(secondary),
    fontColorRgb: hexToRgb(fontColor),
    primaryDocx:   stripHash(primary)   || '111827',
    secondaryDocx: stripHash(secondary) || '6B7280',
    fontColorDocx: stripHash(fontColor) || '1F2937',
  };
};

// ── Image helpers ─────────────────────────────────────────────────────────────

type ImageData = {
  dataUrl: string;
  arrayBuffer: ArrayBuffer;
  width: number;
  height: number;
  format: 'PNG' | 'JPEG';
};

const loadImageData = async (url: string): Promise<ImageData | null> => {
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      if (!url.startsWith('data:')) im.crossOrigin = 'anonymous';
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
      setTimeout(() => reject(new Error('Image load timeout: ' + url)), 10000);
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL('image/png');
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png')
    );
    if (!blob) return null;
    const arrayBuffer = await blob.arrayBuffer();
    return { dataUrl, arrayBuffer, width: img.naturalWidth, height: img.naturalHeight, format: 'PNG' };
  } catch (err) {
    console.warn('[exportPlate] failed to load image:', url, err);
    return null;
  }
};

const collectImageUrls = (nodes: unknown[], out: Set<string> = new Set()): Set<string> => {
  for (const node of nodes) {
    const n = node as { type?: string; url?: string; children?: unknown[] };
    if (n.type === 'img' && n.url) out.add(n.url);
    if (n.children) collectImageUrls(n.children, out);
  }
  return out;
};

export const prefetchImages = async (pages: PlatePage[]): Promise<Map<string, ImageData>> => {
  const urls = new Set<string>();
  for (const page of pages) collectImageUrls(page.plateValue as unknown[], urls);
  const map = new Map<string, ImageData>();
  if (urls.size === 0) return map;
  await Promise.all(Array.from(urls).map(async (u) => {
    const d = await loadImageData(u);
    if (d) map.set(u, d);
  }));
  return map;
};

// ── Block extraction (for PDF) ────────────────────────────────────────────────

type PdfBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string; align?: string }
  | { kind: 'paragraph'; runs: { text: string; bold?: boolean; italic?: boolean; underline?: boolean }[]; align?: string }
  | { kind: 'bullet'; runs: { text: string; bold?: boolean; italic?: boolean }[]; indent: number }
  | { kind: 'numbered'; runs: { text: string; bold?: boolean; italic?: boolean }[]; index: number; indent: number }
  | { kind: 'hr' }
  | { kind: 'quote'; text: string }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'image'; url: string };

const extractRuns = (children: unknown[]): { text: string; bold?: boolean; italic?: boolean; underline?: boolean }[] => {
  const runs: { text: string; bold?: boolean; italic?: boolean; underline?: boolean }[] = [];
  for (const child of children) {
    const c = child as { text?: string; bold?: boolean; italic?: boolean; underline?: boolean; children?: unknown[] };
    if (typeof c.text === 'string') {
      if (c.text) runs.push({ text: c.text, bold: c.bold, italic: c.italic, underline: c.underline });
    } else if (c.children) {
      runs.push(...extractRuns(c.children));
    }
  }
  return runs;
};

const plainText = (children: unknown[]): string =>
  extractRuns(children).map((r) => r.text).join('');

// @platejs/list (the modern ListPlugin used by this editor) stores list items
// as regular block nodes (typically `p`) with `listStyleType` + `indent`
// properties — NOT as nested ul/li/lic. Anything reading the saved Plate
// value for export must check listStyleType to render bullets correctly.
const UNORDERED_LIST_STYLES = new Set(['disc', 'circle', 'square']);
type ListMeta = { family: 'ul' | 'ol'; style: string; indent: number; start?: number };
const readListMeta = (node: unknown): ListMeta | null => {
  const n = node as { listStyleType?: string; indent?: number; listStart?: number };
  const style = n.listStyleType;
  if (!style) return null;
  return {
    family: UNORDERED_LIST_STYLES.has(style) ? 'ul' : 'ol',
    style,
    indent: Math.max(1, n.indent ?? 1),
    start: n.listStart,
  };
};

export const extractBlocks = (nodes: unknown[], blocks: PdfBlock[] = []): PdfBlock[] => {
  let orderedCounter = 1;
  let prevListFamily: 'ul' | 'ol' | null = null;

  for (const node of nodes) {
    const n = node as { type?: string; children?: unknown[]; textAlign?: string; listStart?: number };

    // Indent-list nodes (Plate ListPlugin) come through as p with listStyleType.
    // Detect them BEFORE the type switch so they don't fall into the `p` default.
    const listMeta = readListMeta(n);
    if (listMeta) {
      const runs = extractRuns(n.children ?? []);
      const indent = Math.max(0, listMeta.indent - 1);
      if (listMeta.family === 'ul') {
        prevListFamily = 'ul';
        blocks.push({ kind: 'bullet', runs, indent });
      } else {
        if (prevListFamily !== 'ol') orderedCounter = listMeta.start ?? 1;
        else if (n.listStart != null) orderedCounter = n.listStart;
        prevListFamily = 'ol';
        blocks.push({ kind: 'numbered', runs, index: orderedCounter, indent });
        orderedCounter++;
      }
      continue;
    }
    prevListFamily = null;

    switch (n.type) {
      case 'h1': blocks.push({ kind: 'heading', level: 1, text: plainText(n.children ?? []), align: n.textAlign }); break;
      case 'h2': blocks.push({ kind: 'heading', level: 2, text: plainText(n.children ?? []), align: n.textAlign }); break;
      case 'h3': case 'h4': case 'h5': case 'h6':
        blocks.push({ kind: 'heading', level: 3, text: plainText(n.children ?? []), align: n.textAlign }); break;
      case 'hr': blocks.push({ kind: 'hr' }); break;
      case 'blockquote': blocks.push({ kind: 'quote', text: plainText(n.children ?? []) }); break;
      case 'ul':
        for (const li of (n.children ?? [])) {
          const liNode = li as { children?: unknown[] };
          const lic = (liNode.children ?? []).find((c) => (c as { type?: string }).type === 'lic') as { children?: unknown[] } | undefined;
          const runs = extractRuns(lic?.children ?? liNode.children ?? []);
          blocks.push({ kind: 'bullet', runs, indent: 0 });
        }
        break;
      case 'ol': {
        let idx = 1;
        for (const li of (n.children ?? [])) {
          const liNode = li as { children?: unknown[] };
          const lic = (liNode.children ?? []).find((c) => (c as { type?: string }).type === 'lic') as { children?: unknown[] } | undefined;
          const runs = extractRuns(lic?.children ?? liNode.children ?? []);
          blocks.push({ kind: 'numbered', runs, index: idx++, indent: 0 });
        }
        break;
      }
      case 'table': {
        const rows: string[][] = [];
        for (const row of (n.children ?? [])) {
          const r = row as { children?: unknown[] };
          rows.push((r.children ?? []).map((cell) => plainText((cell as { children?: unknown[] }).children ?? [])));
        }
        if (rows.length) blocks.push({ kind: 'table', rows });
        break;
      }
      case 'img': {
        const url = (n as { url?: string }).url;
        if (url) blocks.push({ kind: 'image', url });
        else blocks.push({ kind: 'paragraph', runs: [{ text: '[Image]', italic: true }] });
        break;
      }
      case 'code_block':
        blocks.push({ kind: 'paragraph', runs: [{ text: '[Code block]', italic: true }] });
        break;
      case 'p': default: {
        const runs = extractRuns(n.children ?? []);
        blocks.push({ kind: 'paragraph', runs: runs.length ? runs : [{ text: '' }], align: n.textAlign });
      }
    }
  }
  return blocks;
};

// ── Puppeteer PDF helpers ─────────────────────────────────────────────────────

// Serializes a PlateJS node tree to an HTML string that mirrors the editor's
// nodeRenderers.tsx exactly — same colors, sizes, borders, margins, weights.
// Logos are intentionally NOT injected here: the cover page in plateValue
// already contains the logo as an `img` node, and adding a header here
// would render it twice.
const makeSerializeNode = (
  colors: BrandColors,
  imageMap: Map<string, ImageData>,
): ((node: unknown) => string) => {
  // primary + alpha hex 18 = ~9% opacity, matches editor's --brand-primary-subtle
  const primarySubtle = `${colors.primary}18`;

  const serializeNode = (node: unknown): string => {
    const n = node as {
      type?: string; children?: unknown[]; text?: string;
      bold?: boolean; italic?: boolean; underline?: boolean;
      strikethrough?: boolean; color?: string; fontSize?: string;
      url?: string; alt?: string; textAlign?: string; align?: string;
    };

    // ── Leaf (text node) ─────────────────────────────────────────────────────
    if (typeof n.text === 'string') {
      if (!n.text) return '';
      let t = n.text
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');
      // Marks applied innermost-first. Order matches editor's PlateLeaf wrappers.
      // BoldLeaf in editor uses color: primary + font-weight: 600 (not 700)
      if (n.bold)          t = `<strong style="color:${colors.primary};font-weight:600">${t}</strong>`;
      if (n.italic)        t = `<em>${t}</em>`;
      if (n.underline)     t = `<u>${t}</u>`;
      if (n.strikethrough) t = `<s>${t}</s>`;
      const spanStyle = [
        n.color    ? `color:${n.color}`        : '',
        n.fontSize ? `font-size:${n.fontSize}` : '',
      ].filter(Boolean).join(';');
      if (spanStyle) t = `<span style="${spanStyle}">${t}</span>`;
      return t;
    }

    // ── Block node ───────────────────────────────────────────────────────────
    const inner = (n.children ?? []).map(serializeNode).join('');
    const ta = n.textAlign ? `text-align:${n.textAlign};` : '';

    switch (n.type) {
      // h1: editor uses text-3xl (30px) font-bold (700) tracking-tight (letter-spacing -0.025em)
      //     pb-2 (8px) mt-6 (24px) mb-3 (12px), color primary, border-bottom 2px primary
      case 'h1':
        return `<h1 style="${ta}color:${colors.primary};font-size:30px;font-weight:700;letter-spacing:-0.025em;margin:24px 0 12px;padding-bottom:8px;border-bottom:2px solid ${colors.primary};line-height:1.2">${inner}</h1>`;
      // h2: text-2xl (24px) font-semibold (600) tracking-tight pb-1 (4px) mt-5 (20px) mb-2 (8px),
      //     color SECONDARY, border-bottom 1px SECONDARY
      case 'h2':
        return `<h2 style="${ta}color:${colors.secondary};font-size:24px;font-weight:600;letter-spacing:-0.025em;margin:20px 0 8px;padding-bottom:4px;border-bottom:1px solid ${colors.secondary};line-height:1.3">${inner}</h2>`;
      // h3: text-xl (20px) font-semibold (600) mt-4 (16px) mb-2 (8px), color SECONDARY, no border
      case 'h3': case 'h4': case 'h5': case 'h6':
        return `<h3 style="${ta}color:${colors.secondary};font-size:20px;font-weight:600;margin:16px 0 8px;line-height:1.3">${inner}</h3>`;
      // blockquote: my-3 (12px) border-l-4 (4px) pl-4 (16px) italic, borderColor secondary, color fontColor
      case 'blockquote':
        return `<blockquote style="${ta}margin:12px 0;border-left:4px solid ${colors.secondary};padding-left:16px;font-style:italic;color:${colors.fontColor}">${inner}</blockquote>`;
      // ul: my-2 (8px vert) ml-6 (24px left) list-disc, color fontColor
      case 'ul':
        return `<ul style="margin:8px 0 8px 24px;padding-left:0;list-style-type:disc;list-style-position:outside;color:${colors.fontColor}">${inner}</ul>`;
      case 'ol':
        return `<ol style="margin:8px 0 8px 24px;padding-left:0;list-style-type:decimal;list-style-position:outside;color:${colors.fontColor}">${inner}</ol>`;
      // li: my-0.5 (2px vert) leading-7 (line-height 1.75)
      case 'li':
        return `<li style="margin:2px 0;line-height:1.75">${inner}</li>`;
      // lic (list item content): editor uses span with leading-7, but inside li this is redundant
      case 'lic':
        return inner;
      // table: w-full border-collapse table-fixed, wrapped in my-3 (12px) div
      case 'table':
        return `<table style="width:100%;border-collapse:collapse;table-layout:fixed;margin:12px 0">${inner}</table>`;
      case 'tr':
        return `<tr>${inner}</tr>`;
      // td: border 1px gray-200 (#e5e7eb), px-3 py-2 (12px 8px), align-top, min-w 60px, color fontColor
      case 'td':
        return `<td style="border:1px solid #e5e7eb;padding:8px 12px;vertical-align:top;min-width:60px;color:${colors.fontColor}">${inner || '&nbsp;'}</td>`;
      // th: same as td + font-semibold, text-left, color primary, background primary-subtle
      case 'th':
        return `<th style="border:1px solid #e5e7eb;padding:8px 12px;font-weight:600;text-align:left;min-width:60px;color:${colors.primary};background-color:${primarySubtle}">${inner || '&nbsp;'}</th>`;
      // img: my-3 wrapper, centered if textAlign center (with max-height 100px), else max-width 100%
      case 'img': {
        const data = imageMap.get(n.url ?? '');
        const src = data ? data.dataUrl : (n.url ?? '');
        if (!src) return '';
        const isCentered = (n.textAlign ?? n.align) === 'center';
        const alt = (n.alt ?? '').replace(/"/g, '&quot;');
        if (isCentered) {
          return `<div style="margin:12px 0;display:flex;justify-content:center"><img src="${src}" alt="${alt}" style="max-height:100px;width:auto"/></div>`;
        }
        return `<div style="margin:12px 0"><img src="${src}" alt="${alt}" style="max-width:100%;height:auto;display:block"/></div>`;
      }
      // hr: my-4 (16px) wrapper div with <hr> inside, border-gray-300 (#d1d5db)
      case 'hr':
        return '<div style="margin:16px 0"><hr style="border:none;border-top:1px solid #d1d5db"/></div>';
      // code_block: my-3 rounded bg-gray-100 p-3 text-sm font-mono
      case 'code_block':
        return `<pre style="margin:12px 0;background:#f3f4f6;padding:12px;border-radius:4px;font-family:'Courier New',Courier,monospace;font-size:14px;white-space:pre-wrap;overflow-x:auto"><code>${inner}</code></pre>`;
      // p: my-2 (8px vert), color fontColor, line-height 1.75
      case 'p': default:
        return `<p style="${ta}margin:8px 0;line-height:1.75;color:${colors.fontColor}">${inner || '&nbsp;'}</p>`;
    }
  };
  return serializeNode;
};

// Walks a flat block array, grouping consecutive indent-list nodes (Plate
// ListPlugin: type 'p' + listStyleType + indent) into proper <ul>/<ol>
// wrappers with <li> children. Non-list nodes are serialized normally.
const serializeBlocksToHtml = (
  nodes: unknown[],
  colors: BrandColors,
  serializeNode: (n: unknown) => string,
): string => {
  let out = '';
  let i = 0;
  while (i < nodes.length) {
    const meta = readListMeta(nodes[i]);
    if (!meta) {
      out += serializeNode(nodes[i]);
      i++;
      continue;
    }
    const family = meta.family;
    const groupStyle = meta.style;
    const groupStart = meta.start;
    const items: string[] = [];
    while (i < nodes.length) {
      const m = readListMeta(nodes[i]);
      if (!m || m.family !== family) break;
      const itemNode = nodes[i] as { children?: unknown[]; textAlign?: string };
      const ta = itemNode.textAlign ? `text-align:${itemNode.textAlign};` : '';
      // Children are text leaves / inline marks — serializeNode handles those.
      const childHtml = (itemNode.children ?? []).map(serializeNode).join('');
      const indentPad = m.indent > 1 ? `margin-left:${(m.indent - 1) * 24}px;` : '';
      items.push(`<li style="${ta}${indentPad}margin:2px 0;line-height:1.75">${childHtml}</li>`);
      i++;
    }
    const startAttr = family === 'ol' && groupStart != null ? ` start="${groupStart}"` : '';
    out += `<${family}${startAttr} style="margin:8px 0 8px 24px;padding-left:0;list-style-type:${groupStyle};list-style-position:outside;color:${colors.fontColor}">${items.join('')}</${family}>`;
  }
  return out;
};

// Serializes a single page's plate value to HTML (block-level nodes only,
// no page wrapper). Used by editor HTML export when caller wants per-page control.
export const serializePlateValueToHtml = (
  plateValue: unknown[],
  colors: BrandColors,
  imageMap: Map<string, ImageData>,
): string => {
  const serializeNode = makeSerializeNode(colors, imageMap);
  return serializeBlocksToHtml(plateValue, colors, serializeNode);
};

export type PageFooterMeta = {
  policyName?: string;
  orgName?: string;
  date?: string;
  // Document classification / sensitivity label (Public / Internal Use / Private
  // / Confidential) shown in the footer. Defaults to "Public" when absent.
  classification?: string;
  // Optional free-text note appended after the classification line.
  note?: string;
};

// Builds the <body>-inner HTML for all pages: each page wrapped in a
// .pdf-page div with A4 dimensions, padding, branded font + color.
// Used for both Puppeteer PDF and HTML download — keeps them identical.
export const buildPlatePagesHtmlBody = (
  pages: PlatePage[],
  colors: BrandColors,
  imageMap: Map<string, ImageData>,
  meta?: PageFooterMeta,
): string => {
  const serializeNode = makeSerializeNode(colors, imageMap);
  const safeFontFamily = toCssFontFamily(colors.fontFamily);
  const coverTocCount = getCoverTocPageCount(pages);

  return pages.map((page, i) => {
    const bgNode = (page as { background?: { type?: string; color?: string } }).background;
    const bg = bgNode?.type === 'solid' ? (bgNode.color ?? '#ffffff') : '#ffffff';

    // Cover (i=0) and TOC (i=1) are full-page layouts — keep them on their own
    // physical page with a forced break and A4 minimum height.
    //
    // Content pages (i>=2) flow naturally: no forced break, no min-height.
    // Reason: a content div whose blocks total slightly more than A4's usable
    // 1011px would otherwise spill one or two lines onto the next page AND
    // then the forced page-break-after creates a half-empty page before the
    // next div starts. Letting content flow lets headings/tables (which already
    // have `break-inside: avoid` / `break-after: avoid` in the CSS) settle on
    // page boundaries cleanly.
    // Each editor page maps 1-to-1 to a PDF page boundary: min-height enforces
    // A4, page-break-after forces a fresh page after the div. This keeps PDF
    // page boundaries identical to the editor's per-page HTML preview — the
    // user expectation is "what you see in the editor is what you get in PDF",
    // page-for-page. Tables that don't fit at a page bottom split between
    // rows (see the `table { break-inside: auto }` CSS in the wrappers) so
    // tight pages don't generate half-empty orphan pages.
    const isLast = i === pages.length - 1;
    const breakStyle = !isLast ? 'page-break-after:always;' : '';
    const minHeightStyle = 'min-height:1123px;';

    const inner = serializeBlocksToHtml(page.plateValue as unknown[], colors, serializeNode);

    // Footer — render-only chrome. Cover (i=0) and TOC (i=1) get no footer.
    // Left side: policy name · © date · org name · Public classification.
    // Right side: page number (N of M).
    const pageLabel = formatPageNumberLabel(i, pages.length, coverTocCount);
    const paddingStyle = pageLabel ? 'padding:56px 56px 96px;' : 'padding:56px;';
    let footer = '';
    if (pageLabel) {
      const dateStr = meta?.date ?? String(new Date().getFullYear());
      const leftLines: string[] = [];
      leftLines.push(`© ${dateStr}`);
      if (meta?.policyName) leftLines.push(meta.policyName);
      if (meta?.orgName) leftLines.push(meta.orgName);
      leftLines.push(meta?.classification ?? 'Public');
      if (meta?.note) leftLines.push(meta.note);
      const leftHtml = leftLines.map(l => `<div style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${l}</div>`).join('');
      footer = `<div style="position:absolute;left:0;right:0;bottom:0;height:72px;display:flex;align-items:flex-end;justify-content:space-between;padding:8px 56px;font-size:9px;line-height:1.5;color:#374151;border-top:1px solid #e5e7eb;box-sizing:border-box"><div style="display:flex;flex-direction:column;overflow:hidden">${leftHtml}</div><div style="white-space:nowrap;flex-shrink:0;margin-left:16px">Page ${pageLabel}</div></div>`;
    }

    return `<div class="pdf-page" style="position:relative;background:${bg};${paddingStyle}box-sizing:border-box;width:794px;${minHeightStyle}${breakStyle}font-family:${safeFontFamily};font-size:14px;line-height:1.6;color:${colors.fontColor}">${inner || '<p style="color:#9ca3af">[Empty page]</p>'}${footer}</div>`;
  }).join('');
};

export const platePagesToPuppeteerHtml = (
  pages: PlatePage[],
  colors: BrandColors,
  imageMap: Map<string, ImageData>,
  meta?: PageFooterMeta,
): string => {
  const safeFontFamily = toCssFontFamily(colors.fontFamily);
  const body = buildPlatePagesHtmlBody(pages, colors, imageMap, meta);

  // Puppeteer's headless Chrome doesn't ship branded webfonts — load from Google Fonts.
  // display=block forces Chrome to wait for the font before rendering.
  const gfontUrl = googleFontsStylesheetHref(colors.fontFamily || DEFAULT_APP_FONT);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
<link href="${gfontUrl}" rel="stylesheet"/>
<style>
  /* @import inside <style> blocks the page render until the CSS arrives,
     giving Puppeteer a second guarantee that fonts are present before print. */
  @import url('${gfontUrl}');
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { font-family: ${safeFontFamily}; }
  /* Inherit the branded font into headings/lists/tables so nothing falls back. */
  h1, h2, h3, h4, h5, h6, p, li, td, th, blockquote, span { font-family: inherit; }
  /* Keep each heading on the same page as the paragraph that follows.
     Without this, h2 lands at bottom of one page and content starts on next. */
  h1, h2, h3, h4, h5, h6 { break-after: avoid-page; page-break-after: avoid; }
  /* Rows + images stay intact, but allow tables themselves to split between
     rows. Otherwise a big table that doesn't fit at the bottom of a page
     forces itself whole to the next page, leaving empty space behind. */
  tr, img { break-inside: avoid; page-break-inside: avoid; }
  table { break-inside: auto; page-break-inside: auto; }
  @page { size: A4; margin: 0; }
</style>
</head>
<body>${body}</body>
</html>`;
};

// Exports pages as a standalone, self-contained .html file (images inlined as
// data URLs, brand font loaded from Google Fonts). The wrapper adds a grey
// canvas, per-page shadow/gap and @media print rules so each A4 page prints
// cleanly. Shared by the branded editor and the Your Policies list so both
// produce byte-identical HTML.
export const exportPlatePagesToHtml = async (
  pages: PlatePage[],
  fileName: string,
  colors: BrandColors,
  meta?: PageFooterMeta,
): Promise<void> => {
  // Pre-fetch every image so the exported HTML is self-contained (works offline).
  // The shared serializer reads data URLs directly from the imageMap.
  const imageMap = await prefetchImages(pages);
  const body = buildPlatePagesHtmlBody(pages, colors, imageMap, meta);

  const docTitle = (fileName || 'Document').replace(/[<>&"]/g, (c) =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c] || c));

  // Load the brand font from Google Fonts so the standalone HTML renders with the
  // right typography in any browser. display=block makes the browser wait for the
  // font before painting text (avoids the default-sans flash, then swap).
  const firstFamily = (colors.fontFamily.split(',')[0] || DEFAULT_APP_FONT).replace(/['"]/g, '').trim();
  const gfontParam = encodeURIComponent(firstFamily).replace(/%20/g, '+');
  const gfontUrl = `https://fonts.googleapis.com/css2?family=${gfontParam}:wght@300;400;500;600;700;800&display=block`;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${docTitle}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="${gfontUrl}" rel="stylesheet">
<style>
  /* Blocking @import — guarantees font CSS is parsed before page paint */
  @import url('${gfontUrl}');
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; padding: 24px; background: #f3f4f6; font-family: ${colors.fontFamily}; }
  /* Pages are separated by 24px vertical gap + soft shadow so each A4 is visually distinct */
  .pdf-page { margin: 0 auto 24px; box-shadow: 0 4px 16px rgba(0,0,0,0.08); border-radius: 4px; }
  .pdf-page:last-of-type { margin-bottom: 0; }
  h1, h2, h3, h4, h5, h6, p, li, td, th, blockquote, span { font-family: inherit; }
  /* Keep headings glued to their following content when printed from browser */
  h1, h2, h3, h4, h5, h6 { break-after: avoid-page; page-break-after: avoid; }
  /* Rows + images stay intact, but allow tables themselves to split between
     rows — otherwise a big table that doesn't fit at the bottom of a page
     forces itself whole to the next page, leaving empty space. */
  tr, img { break-inside: avoid; page-break-inside: avoid; }
  table { break-inside: auto; page-break-inside: auto; }
  @media print {
    body { margin: 0; padding: 0; background: #fff; }
    .pdf-page { margin: 0; box-shadow: none; border-radius: 0; }
  }
  table { width: 100%; }
  img { max-width: 100%; }
</style>
</head>
<body>
${body}
</body>
</html>`;

  const { saveAs } = await import('file-saver');
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  saveAs(blob, `${fileName || 'document'}.html`);
};

export const exportPlatePagesToPuppeteerPdf = async (
  pages: PlatePage[],
  fileName: string,
  colors: BrandColors,
  meta?: PageFooterMeta,
): Promise<void> => {
  // Always go through our own backend proxy (/api/revisr/export/pdf). The browser
  // must never call the external PDF server directly — it returns no CORS headers,
  // so a direct cross-origin fetch is blocked. The backend forwards server-to-server.
  const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3001';

  const imageMap = await prefetchImages(pages);
  const html = platePagesToPuppeteerHtml(pages, colors, imageMap, meta);

  const response = await fetch(`${apiBaseUrl}/api/revisr/export/pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      html,
      options: { format: 'A4', printBackground: true, margin: { top: '0', right: '0', bottom: '0', left: '0' } },
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
    throw new Error(err.error ?? 'PDF generation failed');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = url;
  a.download = `${fileName}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── PDF export ────────────────────────────────────────────────────────────────

export const exportPlatePagesToPdf = async (
  pages: PlatePage[],
  fileName: string,
  brandColors: BrandColors,
): Promise<void> => {
  const imageDataMap = await prefetchImages(pages);
  const { default: jsPDF } = await import('jspdf');

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageWidth  = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const usableWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) { pdf.addPage(); y = margin; }
  };

  const drawRuns = (
    runs: { text: string; bold?: boolean; italic?: boolean }[],
    fontSize: number,
    indent = 0,
    align: 'left' | 'center' | 'right' | 'justify' = 'left',
  ) => {
    const x = margin + indent;
    const wrapWidth = usableWidth - indent;
    const allText = runs.map((r) => r.text).join('');
    if (!allText.trim()) { y += fontSize * 0.4; return; }
    const anyBold   = runs.some((r) => r.bold);
    const anyItalic = runs.some((r) => r.italic);
    const style = anyBold && anyItalic ? 'bolditalic' : anyBold ? 'bold' : anyItalic ? 'italic' : 'normal';
    pdf.setFont('helvetica', style);
    pdf.setFontSize(fontSize);
    const lines = pdf.splitTextToSize(allText, wrapWidth);
    for (const line of lines) {
      ensureSpace(fontSize * 0.5);
      const opts = align !== 'left' ? { align: align as 'center' | 'right' | 'justify', maxWidth: wrapWidth } : undefined;
      const drawX = align === 'center' ? x + wrapWidth / 2 : align === 'right' ? x + wrapWidth : x;
      pdf.text(line, drawX, y, opts);
      y += fontSize * 0.5;
    }
  };

  const drawHr = () => {
    ensureSpace(4);
    pdf.setDrawColor(200);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 4;
  };

  const drawTable = (rows: string[][]) => {
    const cols = Math.max(...rows.map((r) => r.length));
    if (!cols) return;
    const colWidth = usableWidth / cols;
    const cellPad = 2;
    const fontSize = 10;
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setDrawColor(200);
    for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
      const row = rows[rowIdx];
      const cellLines = row.map((cell) => pdf.splitTextToSize(cell || '', colWidth - cellPad * 2));
      const rowH = Math.max(...cellLines.map((l) => l.length)) * fontSize * 0.5 + cellPad * 2;
      ensureSpace(rowH);
      for (let colIdx = 0; colIdx < cols; colIdx++) {
        const cx = margin + colIdx * colWidth;
        if (rowIdx === 0) pdf.setFillColor(245, 245, 245); else pdf.setFillColor(255, 255, 255);
        pdf.rect(cx, y, colWidth, rowH, 'FD');
        if (rowIdx === 0) pdf.setFont('helvetica', 'bold'); else pdf.setFont('helvetica', 'normal');
        const lines = cellLines[colIdx] || [];
        for (let li = 0; li < lines.length; li++) {
          pdf.text(lines[li], cx + cellPad, y + cellPad + fontSize * 0.5 + li * fontSize * 0.5);
        }
      }
      y += rowH;
    }
    y += 4;
  };

  const [pR, pG, pB] = brandColors.primaryRgb;
  const [fR, fG, fB] = brandColors.fontColorRgb;
  const [sR, sG, sB] = brandColors.secondaryRgb;
  pdf.setTextColor(fR, fG, fB);

  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    if (pageIdx > 0) { pdf.addPage(); y = margin; }
    const blocks = extractBlocks(pages[pageIdx].plateValue as unknown[]);
    for (const block of blocks) {
      switch (block.kind) {
        case 'heading': {
          const size = block.level === 1 ? 20 : block.level === 2 ? 16 : 13;
          y += size * 0.3;
          ensureSpace(size * 0.6);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(size);
          pdf.setTextColor(pR, pG, pB);
          for (const line of pdf.splitTextToSize(block.text, usableWidth)) {
            ensureSpace(size * 0.5);
            pdf.text(line, margin, y);
            y += size * 0.55;
          }
          pdf.setTextColor(fR, fG, fB);
          y += 2;
          break;
        }
        case 'paragraph':
          drawRuns(block.runs, 11, 0, (block.align as 'left' | 'center' | 'right' | 'justify') || 'left');
          y += 1;
          break;
        case 'bullet':
          pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11); ensureSpace(6);
          pdf.text('•', margin + 4 + block.indent * 4, y);
          drawRuns(block.runs, 11, 10 + block.indent * 4);
          break;
        case 'numbered':
          pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11); ensureSpace(6);
          pdf.text(`${block.index}.`, margin + 2 + block.indent * 4, y);
          drawRuns(block.runs, 11, 12 + block.indent * 4);
          break;
        case 'hr': drawHr(); break;
        case 'quote': {
          pdf.setFont('helvetica', 'italic'); pdf.setFontSize(11); pdf.setTextColor(fR, fG, fB);
          for (const line of pdf.splitTextToSize(block.text, usableWidth - 8)) {
            ensureSpace(6);
            pdf.setDrawColor(sR, sG, sB); pdf.setLineWidth(1.2);
            pdf.line(margin, y - 4, margin, y + 1);
            pdf.text(line, margin + 8, y);
            y += 5.5;
          }
          pdf.setTextColor(fR, fG, fB); pdf.setLineWidth(0.2);
          break;
        }
        case 'table': drawTable(block.rows); break;
        case 'image': {
          const data = imageDataMap.get(block.url);
          if (!data) {
            pdf.setFont('helvetica', 'italic'); pdf.setFontSize(10); pdf.setTextColor(150, 150, 150);
            ensureSpace(5); pdf.text('[Image unavailable]', margin, y); y += 5;
            pdf.setTextColor(fR, fG, fB);
            break;
          }
          const aspect = data.height / data.width;
          const maxImgWidth = Math.min(usableWidth, 150);
          const imgW = Math.min(data.width * 0.264583, maxImgWidth);
          const imgH = imgW * aspect;
          ensureSpace(imgH + 4);
          try {
            pdf.addImage(data.dataUrl, data.format, margin, y, imgW, imgH, undefined, 'FAST');
          } catch {
            pdf.setFont('helvetica', 'italic'); pdf.setFontSize(10); pdf.setTextColor(150, 150, 150);
            pdf.text('[Image unavailable]', margin, y); pdf.setTextColor(fR, fG, fB);
          }
          y += imgH + 4;
          break;
        }
      }
    }
  }

  pdf.save(`${fileName}.pdf`);
};

// ── DOCX export ───────────────────────────────────────────────────────────────

export const exportPlatePagesToDocx = async (
  pages: PlatePage[],
  fileName: string,
  brandColors: BrandColors,
  meta?: PageFooterMeta,
): Promise<void> => {
  const imageDataMap = await prefetchImages(pages);
  const {
    Document, Packer, Paragraph, TextRun, HeadingLevel,
    Table, TableRow, TableCell, WidthType, AlignmentType,
    BorderStyle, ImageRun, PageBreak, Footer, PageNumber,
  } = await import('docx');

  type Leaf = {
    text?: string; bold?: boolean; italic?: boolean; underline?: boolean;
    strikethrough?: boolean; color?: string; fontSize?: string;
    children?: unknown[];
  };

  // DOCX uses half-points for font size (e.g. 22 = 11pt).
  // PlateJS leaf fontSize can be px / pt / em / rem — convert each correctly.
  // Base size is 22 half-points (11pt = the document default), so 1em = 22.
  // Without unit awareness, "1.125em" was being read as 1.125 → ~2 half-points (≈1pt),
  // which is why the TOC was rendering microscopically small in Word.
  const sizeToHalfPoints = (sz?: string): number | undefined => {
    if (!sz) return undefined;
    const match = String(sz).trim().match(/^([\d.]+)\s*(px|pt|em|rem|%)?$/i);
    if (!match) return undefined;
    const n = parseFloat(match[1]);
    if (!Number.isFinite(n)) return undefined;
    const unit = (match[2] ?? 'px').toLowerCase();
    switch (unit) {
      case 'pt':            return Math.round(n * 2);    // 1pt = 2 half-points
      case 'em': case 'rem':return Math.round(n * 22);   // 1em = base 11pt = 22 half-points
      case '%':             return Math.round((n / 100) * 22);
      case 'px':
      default:              return Math.round(n * 1.5);  // 1px ≈ 0.75pt = 1.5 half-points
    }
  };

  const buildRuns = (
    children: unknown[],
    defaultColor: string = brandColors.fontColorDocx,
  ): InstanceType<typeof TextRun>[] =>
    children.flatMap((leaf): InstanceType<typeof TextRun>[] => {
      const l = leaf as Leaf;
      if (typeof l.text === 'string') {
        if (!l.text) return [];
        // Editor's BoldLeaf renders bold text in primary color + weight 600.
        // Honor explicit leaf color first, then bold-implies-primary, then default.
        const resolvedColor = l.color
          ? l.color.replace('#', '')
          : (l.bold ? brandColors.primaryDocx : defaultColor);
        return [new TextRun({
          text: l.text,
          bold: l.bold,
          italics: l.italic,
          underline: l.underline ? {} : undefined,
          strike: l.strikethrough,
          color: resolvedColor,
          size: sizeToHalfPoints(l.fontSize),
        })];
      }
      return buildRuns(l.children ?? [], defaultColor);
    });

  const alignFor = (a?: string) => {
    switch (a) {
      case 'center':  return AlignmentType.CENTER;
      case 'right':   return AlignmentType.RIGHT;
      case 'justify': return AlignmentType.JUSTIFIED;
      default:        return AlignmentType.LEFT;
    }
  };

  type DocxItem = InstanceType<typeof Paragraph> | InstanceType<typeof Table>;

  const serializeToDocxChildren = (plateValue: unknown[], listLevel = 0): DocxItem[] => {
    const items: DocxItem[] = [];
    for (const node of plateValue) {
      const n = node as { type?: string; children?: unknown[]; textAlign?: string; url?: string };
      const align = alignFor(n.textAlign);

      // Indent-list nodes (Plate ListPlugin) come through as p with listStyleType.
      // Convert them to native DOCX bullets/numbering before the type switch so
      // they don't fall into the `p` default and render as plain paragraphs.
      const listMeta = readListMeta(n);
      if (listMeta) {
        const level = Math.max(0, listMeta.indent - 1);
        items.push(new Paragraph({
          children: buildRuns(n.children ?? []),
          ...(listMeta.family === 'ol'
            ? { numbering: { reference: 'default-ordered', level } }
            : { bullet: { level } }),
          alignment: align,
          spacing: { before: 40, after: 40 },
        }));
        continue;
      }

      switch (n.type) {
        // h1: primary color, 2px bottom border in primary (matches editor borderBottom)
        case 'h1':
          items.push(new Paragraph({
            children: buildRuns(n.children ?? [], brandColors.primaryDocx),
            heading: HeadingLevel.HEADING_1,
            alignment: align,
            spacing: { before: 240, after: 120 },
            border: { bottom: { style: BorderStyle.SINGLE, size: 16, color: brandColors.primaryDocx, space: 4 } },
          }));
          break;
        // h2: SECONDARY color, 1px bottom border in secondary
        case 'h2':
          items.push(new Paragraph({
            children: buildRuns(n.children ?? [], brandColors.secondaryDocx),
            heading: HeadingLevel.HEADING_2,
            alignment: align,
            spacing: { before: 200, after: 100 },
            border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: brandColors.secondaryDocx, space: 2 } },
          }));
          break;
        // h3: SECONDARY color, no border
        case 'h3':
          items.push(new Paragraph({
            children: buildRuns(n.children ?? [], brandColors.secondaryDocx),
            heading: HeadingLevel.HEADING_3,
            alignment: align,
            spacing: { before: 160, after: 80 },
          }));
          break;
        case 'hr':
          items.push(new Paragraph({ children: [], border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'E5E7EB' } }, spacing: { before: 120, after: 120 } }));
          break;
        case 'blockquote':
          items.push(new Paragraph({ children: buildRuns(n.children ?? []), indent: { left: 360 }, border: { left: { style: BorderStyle.SINGLE, size: 18, color: brandColors.secondaryDocx, space: 8 } }, spacing: { before: 80, after: 80 } }));
          break;
        case 'ul':
        case 'ol': {
          const isOrdered = n.type === 'ol';
          for (const li of (n.children ?? [])) {
            const liNode = li as { children?: unknown[] };
            const licContent = (liNode.children ?? []).find((c) => (c as { type?: string }).type === 'lic') as { children?: unknown[] } | undefined;
            items.push(new Paragraph({
              children: buildRuns(licContent?.children ?? liNode.children ?? []),
              ...(isOrdered
                ? { numbering: { reference: 'default-ordered', level: listLevel } }
                : { bullet: { level: listLevel } }),
              alignment: align,
              spacing: { before: 40, after: 40 },
            }));
            const nestedLists = (liNode.children ?? []).filter((c) => {
              const t = (c as { type?: string }).type;
              return t === 'ul' || t === 'ol';
            });
            for (const nested of nestedLists) {
              items.push(...serializeToDocxChildren([nested], listLevel + 1));
            }
          }
          break;
        }
        case 'table': {
          const rows = (n.children ?? []).map((row) => {
            const r = row as { children?: unknown[] };
            const cells = (r.children ?? []).map((cell) => {
              const c = cell as { type?: string; children?: unknown[] };
              const cellChildren = serializeToDocxChildren(c.children ?? []);
              return new TableCell({
                children: cellChildren.length ? cellChildren : [new Paragraph({ children: [] })],
                width: { size: 100 / (r.children?.length || 1), type: WidthType.PERCENTAGE },
              });
            });
            return new TableRow({ children: cells });
          });
          if (rows.length) {
            items.push(new Table({
              rows,
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top:              { style: BorderStyle.SINGLE, size: 4, color: 'D1D5DB' },
                bottom:           { style: BorderStyle.SINGLE, size: 4, color: 'D1D5DB' },
                left:             { style: BorderStyle.SINGLE, size: 4, color: 'D1D5DB' },
                right:            { style: BorderStyle.SINGLE, size: 4, color: 'D1D5DB' },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: 'D1D5DB' },
                insideVertical:   { style: BorderStyle.SINGLE, size: 4, color: 'D1D5DB' },
              },
            }));
            items.push(new Paragraph({ children: [] }));
          }
          break;
        }
        case 'code_block':
          items.push(new Paragraph({
            children: [new TextRun({ text: (n.children ?? []).map((c) => (c as Leaf).text ?? '').join(''), font: 'Consolas', size: 20 })],
            shading: { type: 'clear', color: 'auto', fill: 'F3F4F6' } as never,
            spacing: { before: 80, after: 80 },
          }));
          break;
        case 'img': {
          if (!n.url) break;
          const data = imageDataMap.get(n.url);
          if (data) {
            const maxPx = 500;
            const w = Math.min(data.width, maxPx);
            const h = data.height * (w / data.width);
            items.push(new Paragraph({
              children: [new ImageRun({ data: data.arrayBuffer, transformation: { width: w, height: h }, type: 'png' } as never)],
              alignment: AlignmentType.CENTER,
              spacing: { before: 120, after: 120 },
            }));
          } else {
            items.push(new Paragraph({
              children: [new TextRun({ text: '[Image unavailable]', italics: true, color: '9CA3AF' })],
              alignment: AlignmentType.CENTER,
            }));
          }
          break;
        }
        case 'p': default:
          items.push(new Paragraph({ children: buildRuns(n.children ?? []), alignment: align, spacing: { before: 60, after: 60, line: 320 } }));
      }
    }
    return items;
  };

  // Build the body for a list of pages, inserting a page break between each so
  // every editor page maps to its own Word page.
  const pageChildren = (pageList: PlatePage[]): DocxItem[] => {
    const out: DocxItem[] = [];
    pageList.forEach((page, i) => {
      if (i > 0) out.push(new Paragraph({ children: [new PageBreak()] }));
      out.push(...serializeToDocxChildren(page.plateValue as unknown[]));
    });
    return out;
  };

  // Split into two Word sections so page numbers appear only on content pages:
  //   Section A = cover + TOC (no footer)
  //   Section B = content pages, numbered "N of M" starting at 1.
  // NumberOfPages can't be used for M — it counts cover + TOC too — so the
  // content-page total is embedded as a literal.
  const coverTocCount = getCoverTocPageCount(pages);
  const coverTocPages = pages.slice(0, coverTocCount);
  const contentPages = pages.slice(coverTocCount);
  const contentPageCount = getContentPageCount(pages.length, coverTocCount);
  const pageMargin = { top: 1134, right: 1134, bottom: 1134, left: 1134 };

  const docxFontFamily = brandColors.fontFamily.replace(/['"]/g, '').trim() || DEFAULT_APP_FONT;

  type DocSection = NonNullable<ConstructorParameters<typeof Document>[0]>['sections'][number];
  const docSections: DocSection[] = [];
  if (coverTocPages.length) {
    docSections.push({
      properties: { page: { margin: pageMargin } },
      children: pageChildren(coverTocPages) as never[],
    });
  }
  if (contentPages.length) {
    docSections.push({
      properties: { page: { margin: pageMargin, pageNumbers: { start: 1 } } },
      footers: {
        default: new Footer({
          children: [
            // Line 1: © Date
            new Paragraph({
              children: [new TextRun({ text: `© ${meta?.date ?? String(new Date().getFullYear())}`, color: '374151', size: 16})],
              spacing: { before: 0, after: 0 },
            }),
            // Line 2: Policy name (omit if absent)
            ...(meta?.policyName ? [new Paragraph({
              children: [new TextRun({ text: meta.policyName, color: '374151', size: 16})],
              spacing: { before: 0, after: 0 },
            })] : []),
            // Line 3: Org name (omit if absent)
            ...(meta?.orgName ? [new Paragraph({
              children: [new TextRun({ text: meta.orgName, color: '374151', size: 16})],
              spacing: { before: 0, after: 0 },
            })] : []),
            // Line 4: Classification — left | Page N of M — right (tab stop)
            new Paragraph({
              tabStops: [{ type: 'right' as never, position: 9360 }],
              children: [
                new TextRun({ text: meta?.classification ?? 'Public', color: '374151', size: 16}),
                new TextRun({ text: '\t', size: 16 }),
                new TextRun({ text: 'Page ', color: '374151', size: 16}),
                new TextRun({ children: [PageNumber.CURRENT], color: '374151', size: 16}),
                new TextRun({ text: ` of ${contentPageCount}`, color: '374151', size: 16}),
              ],
              spacing: { before: 0, after: 0 },
            }),
            // Line 5: Optional free-text note (omit if absent)
            ...(meta?.note ? [new Paragraph({
              children: [new TextRun({ text: meta.note, color: '374151', size: 16 })],
              spacing: { before: 0, after: 0 },
            })] : []),
          ],
        }),
      },
      children: pageChildren(contentPages) as never[],
    });
  }
  // Guard: a document with no pages still needs one section.
  if (!docSections.length) {
    docSections.push({ properties: { page: { margin: pageMargin } }, children: [] });
  }

  const doc = new Document({
    creator: 'Revisr',
    title: fileName,
    styles: {
      default: {
        document: {
          run: { font: docxFontFamily, size: 22, color: brandColors.fontColorDocx },
        },
      },
    },
    numbering: {
      config: [{
        reference: 'default-ordered',
        levels: [
          { level: 0, format: 'decimal',     text: '%1.', alignment: AlignmentType.START, style: { paragraph: { indent: { left: 360,  hanging: 260 } } } },
          { level: 1, format: 'lowerLetter', text: '%2.', alignment: AlignmentType.START, style: { paragraph: { indent: { left: 720,  hanging: 260 } } } },
          { level: 2, format: 'lowerRoman',  text: '%3.', alignment: AlignmentType.START, style: { paragraph: { indent: { left: 1080, hanging: 260 } } } },
        ],
      }],
    },
    sections: docSections,
  });

  const blob = await Packer.toBlob(doc);
  try {
    const { saveAs } = await import('file-saver');
    saveAs(blob, `${fileName}.docx`);
  } catch {
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url; a.download = `${fileName}.docx`; a.click();
    URL.revokeObjectURL(url);
  }
};
